import { useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, LoaderCircle } from "lucide-react";
import { useCustomArtStore } from "../../../stores/custom-art-store";
import { dispatchScheduleEvent } from "../../../lib/schedule-history/dispatch";
import { registerImage } from "../../../lib/schedule-history/image-registry";
import { cn } from "../../../lib/utils";

export function ArtDropzone() {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [dragging, setDragging] = useState(false);

  const loadFiles = async (files: File[]) => {
    if (busy.current || files.length === 0) return;
    busy.current = true;
    setLoading(true);
    setFailed(false);
    const results = await Promise.allSettled(files.map(async file => {
      const image = new Image();
      const url = URL.createObjectURL(file);
      try {
        image.src = url;
        await image.decode();
        // Retain valid URLs for thumbnails, exports, and history replay.
        return { imageId: registerImage(image), name: file.name };
      } catch (error) {
        URL.revokeObjectURL(url);
        throw error;
      }
    }));
    const images = results.flatMap(result => result.status === "fulfilled" ? [result.value] : []);
    if (images.length > 0) {
      dispatchScheduleEvent({ type: "art/addImages", images });
      useCustomArtStore.getState().selectLayer(images.at(-1)!.imageId);
    }
    setFailed(results.some(result => result.status === "rejected"));
    busy.current = false;
    setLoading(false);
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    void loadFiles(Array.from(event.dataTransfer.files));
  };

  return (
    <div onDragOver={event => { event.preventDefault(); setDragging(true); }}
      onDragLeave={event => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setDragging(false);
      }} onDrop={onDrop} className="space-y-2">
      <button type="button" disabled={loading} onClick={() => input.current?.click()}
        className={cn(
          "flex w-full flex-col items-center justify-center gap-2 rounded border-2 border-dashed bg-neutral-50 px-4 py-5 text-neutral-500 transition-colors hover:border-neutral-400 hover:text-neutral-900 disabled:cursor-wait",
          dragging ? "border-neutral-900 text-neutral-900" : "border-neutral-300",
        )}>
        {loading ? <LoaderCircle className="size-7 animate-spin" /> : <ImagePlus className="size-7" />}
        <span className="text-xs font-medium">{t("art.dropHint")}</span>
      </button>
      <input ref={input} type="file" accept="image/*" multiple className="hidden" onChange={event => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = "";
        void loadFiles(files);
      }} />
      {failed && <p role="alert" className="text-xs text-red-600">{t("art.uploadError")}</p>}
    </div>
  );
}
